import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { FC, useMemo, useState } from 'react';
import strings from '../../../../../../common/Translation/Translate';
import { getHighlightedDaysForMonth, UnavailabilityDateRange } from './unavailabilityDateUtils';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

type UnavailabilityCalendarProps = {
  dates: UnavailabilityDateRange[];
};

export const UnavailabilityCalendar: FC<UnavailabilityCalendarProps> = ({ dates }) => {
  const [visibleMonth, setVisibleMonth] = useState<Dayjs>(() => dayjs().startOf('month'));

  const highlightedDays = useMemo(
    () => getHighlightedDaysForMonth(dates, visibleMonth),
    [dates, visibleMonth],
  );

  const calendarDays = useMemo(() => {
    const days: Dayjs[] = [];
    let cursor = visibleMonth.startOf('month').startOf('week');
    const last = visibleMonth.endOf('month').endOf('week');
    while (!cursor.isAfter(last, 'day')) {
      days.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    return days;
  }, [visibleMonth]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: 'background.paper',
      }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {strings.venueConfig_unavailability_calendar_title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {strings.venueConfig_unavailability_calendar_hint}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton
          size="small"
          aria-label="previous month"
          onClick={() => setVisibleMonth(visibleMonth.subtract(1, 'month'))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="subtitle2" fontWeight={600}>
          {visibleMonth.format('MMMM YYYY')}
        </Typography>
        <IconButton
          size="small"
          aria-label="next month"
          onClick={() => setVisibleMonth(visibleMonth.add(1, 'month'))}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          textAlign: 'center',
        }}>
        {WEEKDAY_LABELS.map(label => (
          <Typography key={label} variant="caption" color="text.secondary" fontWeight={600} sx={{ py: 0.5 }}>
            {label}
          </Typography>
        ))}
        {calendarDays.map(day => {
          const inMonth = day.month() === visibleMonth.month();
          const isHighlighted = inMonth && highlightedDays.includes(day.date());
          return (
            <Box
              key={day.format('YYYY-MM-DD')}
              sx={{
                py: 0.75,
                borderRadius: 1,
                fontSize: '0.875rem',
                color: inMonth ? 'text.primary' : 'text.disabled',
                bgcolor: isHighlighted ? 'warning.main' : 'transparent',
                fontWeight: isHighlighted ? 600 : 400,
                ...(isHighlighted && { color: 'warning.contrastText' }),
              }}>
              {day.date()}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
